/**
 * lab-uitslag.js — het resultaatbestand van het lab als tabel: bepalingen onder elkaar,
 * analysemonsters naast elkaar, zoals op het analysecertificaat.
 *
 * Het bestand (zie backend utils/sikb-labresultaat.js) geeft per analysemonster een lijst
 * meetwaarden zonder stofgroep. De groepen hieronder volgen de kopjes van het certificaat van
 * SGS; ze worden herkend aan de naam van de bepaling, want een componentcode zegt niemand iets.
 */

/**
 * Eenheidcodes uit de codelijst van het lab die we zeker kennen. Gemeten aan echte
 * resultaatbestanden naast het certificaat van dezelfde opdracht. Een code die hier niet staat
 * tonen we als code: een verzonnen eenheid naast een meetwaarde is erger dan geen eenheid.
 */
export const EENHEDEN = {
  14: 'mg/kg ds',
};

const GROEPEN = [
  ['Metalen', /^(antimoon|arseen|barium|beryllium|cadmium|chroom|kobalt|koper|kwik|lood|molybdeen|nikkel|seleen|thallium|tin|vanadium|zilver|zink)\b/i],
  ['Polycyclische aromatische koolwaterstoffen (PAK)', /\bpak\b|naftaleen|antraceen|fenantreen|fluoranteen|chryseen|pyreen|peryleen|acenafte/i],
  ['Polychloorbifenylen (PCB)', /\bpcb/i],
  ['Minerale olie', /olie|fractie c\d/i],
  ['Vluchtige aromaten', /benzeen|tolueen|xyle|styreen/i],
  ['Gechloreerde koolwaterstoffen', /chloor|chloride|chloroform|broom/i],
  ['Asbest', /asbest|serpentijn|amfibool|betrouwb|chrysotiel|amosiet|crocidoliet|anthophylliet|tremoliet|actinoliet|hechtgebonden/i],
  ['Algemeen', /droge stof|organische stof|gloeiverlies|lutum|korrel|voorbehandeling|gewicht|aangeleverd/i],
];

/** De stofgroep van een bepaling; wat we niet herkennen valt onder 'Overig'. */
export function groepVan(label) {
  const naam = String(label || '');
  return GROEPEN.find(([, patroon]) => patroon.test(naam))?.[0] || 'Overig';
}

/** Onder de rapportagegrens: het lab schrijft dan '<0.2' en zet referentiecode 3. */
export function onderGrens(w) {
  return String(w?.referenceCode || '') === '3' || /^\s*</.test(String(w?.valueText || ''));
}

/**
 * De meetwaarde zoals het certificaat hem afdrukt. Het resultaatbestand zet er Engels in
 * ('not detected'), het certificaat van hetzelfde lab 'niet gedetecteerd': we volgen het
 * certificaat. Wat we niet herkennen blijft staan zoals het lab het schreef.
 */
export function meetwaardeTekst(w) {
  if (!w) return '';
  const tekst = String(w.valueText || '').trim();
  if (!tekst) return w.value ?? '—';
  if (/^not detected$/i.test(tekst)) return 'niet gedetecteerd';
  if (/^not applicable$/i.test(tekst)) return 'niet van toepassing';
  return tekst;
}

/**
 * De tabel: kolommen = analysemonsters in de volgorde van het lab, groepen met rijen = één
 * bepaling (component + eenheid) met per kolom de meetwaarde of null.
 */
export function uitslagTabel(results) {
  const monsters = [...(results?.samples || [])]
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const rijen = new Map();
  monsters.forEach((m, kolom) => {
    for (const w of m.results || []) {
      const sleutel = `${w.componentCode || w.label}|${w.unitCode || ''}`;
      if (!rijen.has(sleutel)) {
        rijen.set(sleutel, {
          label: w.label || w.componentCode || '—',
          eenheidCode: w.unitCode || '',
          eenheid: EENHEDEN[w.unitCode] || '',
          cellen: monsters.map(() => null),
        });
      }
      rijen.get(sleutel).cellen[kolom] = w;
    }
  });
  const groepen = new Map();
  for (const rij of rijen.values()) {
    const naam = groepVan(rij.label);
    if (!groepen.has(naam)) groepen.set(naam, []);
    groepen.get(naam).push(rij);
  }
  // Algemeen voorop, zoals op het certificaat; de rest in de volgorde waarin het lab ze gaf.
  const volgorde = [...groepen.keys()].sort((a, b) => (b === 'Algemeen') - (a === 'Algemeen'));
  return {
    kolommen: monsters.map(m => ({ name: m.name || '—', analysisSampleId: m.analysisSampleId || null })),
    groepen: volgorde.map(naam => ({ naam, rijen: groepen.get(naam) })),
    aantal: rijen.size,
  };
}
